import type { CatalogEvent } from "@/features/catalog/mediators/catalogEvents";
import {
  initialWheelReloadState,
  recordEdgeWheel,
  resetWheelReloadSequence,
  type WheelReloadState,
} from "@/features/thread/utils/wheelReload";
import {
  createMediator,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";

export type CatalogWheelEvent =
  | { type: "catalog-wheel.enabled"; enabled: boolean }
  | { type: "catalog-wheel.scroll"; now: number }
  | {
      type: "catalog-wheel.observed";
      deltaY: number;
      deltaMode: number;
      atEdge: boolean;
      hasModifier: boolean;
      now: number;
    };
type WheelState = { enabled: boolean; gesture: WheelReloadState };
type RefreshEffect = Extract<
  CatalogEvent,
  { type: "catalog/refresh-requested" }
>;

export function isCatalogWheelEvent(
  event: TypedEvent,
): event is CatalogWheelEvent {
  return (
    event.type === "catalog-wheel.enabled" ||
    event.type === "catalog-wheel.scroll" ||
    event.type === "catalog-wheel.observed"
  );
}

export function createCatalogWheelMediator(
  enabled: boolean,
  runEffect: (effect: RefreshEffect) => void,
) {
  return createMediator<WheelState, CatalogWheelEvent, RefreshEffect>(
    { enabled, gesture: initialWheelReloadState() },
    (state, event) => {
      if (event.type === "catalog-wheel.enabled") {
        return {
          status: "handled",
          state: {
            enabled: event.enabled,
            gesture: event.enabled ? state.gesture : initialWheelReloadState(),
          },
        };
      }
      if (!state.enabled) return { status: "handled", state };
      if (event.type === "catalog-wheel.scroll") {
        return {
          status: "handled",
          state: {
            ...state,
            gesture: resetWheelReloadSequence(state.gesture, event.now),
          },
        };
      }
      if (event.hasModifier) return { status: "handled", state };
      const result = recordEdgeWheel(
        state.gesture,
        event.deltaY,
        event.atEdge,
        event.now,
        event.deltaMode,
      );
      return {
        status: "handled",
        state: { ...state, gesture: result.state },
        effects: result.reload
          ? [{ type: "catalog/refresh-requested", manual: true }]
          : [],
      };
    },
    { runEffect },
  );
}
